<script lang='ts'>
export default {
  name: 'KkNpsDetail',
}
</script>

<script setup lang='ts'>
import { KkButton } from '../../../../components/base'
import ChartHorizontalBar from '../../../../components/ChartHorizontalBar.vue'
import type {TargetQuestionNps, QuestionNpsOption, Nps} from '../../types'
import { npsList, decimalPlace } from '../../../../modules/analysis'
import { roundDecimal } from '../../../../modules/decimalCalc'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js'

const { t } = useI18n()

interface Emits {
  (event: 'change-display-type', value: string): void
}

interface TableData{
  id: Nps
  name: string
  elements: TableDataElement[]
}

interface TableDataElement{
  count: number
  rate: number
}

interface Props {
  dataList: TargetQuestionNps[]
  graphDisplayType: 'count' | 'ratio'
  comparableType: 'average' | 'period'
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const npsColorList = npsList as { id: Nps, color: string }[]

const questionOptions = npsColorList.map(item => ({
  id: item.id, name: t(`pages.companies.analysis.surveys.detail.npsName.${item.id}`)
}))

const chartData = computed((): ChartData<'bar'> => {
  return {
    labels: props.dataList.map(d => d.name),
    datasets: questionOptions.map((option) => {
      return {
        data: calcChartData(option),
        backgroundColor: labelColor(option.id),
        label: option.name,
        barThickness: 25,
      }
    })
  }
})

const chartHeight = computed((): number => {
  return props.dataList.length * 70 + 50
})

const hasComparedValue = computed((): boolean => {
  return props.dataList.length > 1
})

const tableData = computed<TableData[]>(() => {
  return questionOptions.map((option) => {
    return {
      id: option.id,
      name: option.name,
      elements: props.dataList.map(data => {
        return {
          count: roundDecimal(data.value[option.id].value, decimalPlace),
          rate: roundDecimal(data.value[option.id].rate * 100, decimalPlace)
        }
      })
    }
  })
})

// graph option
const optionsForChartHorizontalBar = computed((): ChartOptions<'bar'> => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        max: props.graphDisplayType === 'ratio' ? 100 : undefined,
        min: 0,
        ticks: {
          precision: 0
        },
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        position: 'top'
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        callbacks: {
          label: getTooltipLabel
        }
      },
    },
  }
})

const graphDisplayTypeLabel = computed((): string => {
  if (props.graphDisplayType === 'count') {
    return t('pages.companies.analysis.surveys.displayByRatio')
  }
  return t('pages.companies.analysis.surveys.displayByCount')
})

const targetTypeLabel = computed((): string => {
  if (props.comparableType === 'average') {
    return t('pages.companies.analysis.surveys.detail.table.answerCount')
  }
  return t('pages.companies.analysis.surveys.detail.table.answerCountDuringTargetPeriod')
})

const comparableTypeLabel = computed((): string => {
  if (props.comparableType === 'average') {
    return t('pages.companies.analysis.surveys.detail.table.averageAnswerCount')
  }
  return t('pages.companies.analysis.surveys.detail.table.answerCountDuringComparedPeriod')
})

const getTooltipLabel = (context: TooltipItem<'bar'>): string => {
  const unit: string = props.graphDisplayType === 'count' ? '' : t('labels.percentage')
  return `${context.dataset.label}: ${context.formattedValue}${unit}`
}

const changeDisplayType = (): void => {
  emit('change-display-type', props.graphDisplayType == 'count' ? 'ratio' : 'count')
}

const calcChartData = (option: QuestionNpsOption): number[] => {
  return props.dataList.map(data => {
    if (props.graphDisplayType === 'count') {
      return roundDecimal(data.value[option.id].value, decimalPlace)
    } else {
      return roundDecimal(data.value[option.id].rate * 100, decimalPlace)
    }
  })
}

const labelColor = (id: Nps): string => {
  const nps = npsColorList.find(item => item.id === id)
  if (nps == null) throw new Error('npsColorListの対象データがありません。')
  return nps.color
}
</script>

<template>
  <div>
    <KkButton
      color="secondary"
      :text="graphDisplayTypeLabel"
      @click="changeDisplayType"
    />
    <!-- グラフ -->
    <div class="grid">
      <div class="graph">
        <ChartHorizontalBar
          :chart-data="chartData"
          :options="optionsForChartHorizontalBar"
          :styles="{ 'height': `${chartHeight}px`, 'width': '100%', 'position': 'relative', 'transition': '.4s' }"
        />
      </div>
      <div class="nps">
        <template v-for="(data, i) in dataList" :key="i">
          <dl class="nps__row">
            <dt class="nps__label">NPS：</dt>
            <dd class="nps__value">{{ data.value.score }}</dd>
          </dl>
        </template>
      </div>
    </div>
    <!-- テーブル -->
    <div class="p-table">
      <div class="p-table__content">
        <table class="compare__table">
          <thead>
            <tr>
              <th>{{ t('pages.companies.analysis.surveys.detail.table.questionOption') }}</th>
              <th colspan="2">{{ targetTypeLabel }}</th>
              <th v-if="hasComparedValue" colspan="2">
                {{ comparableTypeLabel }}
              </th>
            </tr>
            <tr class="p-table__content__space">
              <td :colSpan="hasComparedValue ? 5 : 3" />
            </tr>
          </thead>
          <tbody>
            <tr v-for="(option, i) in tableData" :key="i">
              <td>
                <span class="label__dot" :style="{ 'background': `${ labelColor(option.id) }` }" />
                {{ option.name }}
              </td>
              <td>{{ option.elements[0].count.toLocaleString() }}{{ t('labels.countUnit') }}</td>
              <td>{{ option.elements[0].rate }}{{ t('labels.percentage') }}</td>
              <template v-if="hasComparedValue">
                <td>{{ option.elements[1].count.toLocaleString() }}{{ t('labels.countUnit') }}</td>
                <td>{{ option.elements[1].rate }}{{ t('labels.percentage') }}</td>
              </template>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "../../../../../../stylesheets/biz_spa/foundation/_var.scss";
.graph {
  width: 80%;
  margin: 32px auto;
}
.compare__table {
  width: auto;
  min-width: 100%;
  th:first-child {
    width: 50%;
    @media screen and (max-width: 768px) {
      width: 40%;
    }
  }
  td {
    white-space: nowrap;
  }
  td:first-child {
    background-color: $color-base-i;
    word-break: break-all;
  }
  td:not(:first-child) {
    text-align: right;
  }
}
.label__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 4px;
  border-radius: 50%;
}
.grid {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
}
.nps {
  margin-left: 24px;
  padding-right: 54px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  @media screen and (max-width: 768px) {
    margin-left: 12px;
  }
  &__row {
    display: flex;
    align-items: baseline;
    &:not(:last-child) {
      padding-bottom: 40px;
    }
  }
  &__label {
    margin-right: 4px;
    font-size: 10px;
    line-height: 1.7;
  }
  &__value {
    font-size: 18px;
    line-height: 1.5;
  }
}
</style>
